/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { Size } from '../utils/UNSAFE_size';
import { classNames } from '../utils/UNSAFE_classNames';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { ComponentMessageItem } from '../UNSAFE_ComponentMessage';

import { getAriaLabel, getStyles } from './FilePickerUtils';
import { useFilepickerElementHelper } from './useFilePickerElement';

import { ComponentChildren } from 'preact';
import { useState, useRef, useImperativeHandle } from 'preact/hooks';
import { ForwardedRef } from 'preact/compat';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { type TestIdProps, useTestId } from '../hooks/UNSAFE_useTestId';

import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { FilePickerStyles, FilePickerVariantOptions } from './themes/FilePickerStyles.css';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { FilePickerRedwoodTheme } from './themes/redwood/FilePickerTheme';
import { FilePickerDefaultTriggerContent } from './FilePickerDefaultTriggerContent';
// TODO: Andy's comment below JET-53230
// The FilePicker supports two very different use cases: default and custom file pickers. Should we consider dropping the wrapper and instead exporting the two underlying components (with the appropriate property types) directly?
// If not, perhaps we should look into defining the Props type as a discriminated union such that primaryText/secondaryText props are only valid when children is not specified?

type Props = TestIdProps & {
  /**
   * An array of strings of allowed MIME types or file extensions that can be uploaded; this is unlike the accept attribute of the html &lt;input> element that accepts a simple comma-delimited string. If not specified, all file types will be accepted.
   * <p>Note: If accept is specified, files with empty string type will be rejected if no match found in the "accept" value.   */
  accept?: string[];

  /**
   * Specifies the preferred facing mode for the device's
   * <a href="https://www.w3.org/TR/html-media-capture/#dom-htmlinputelement-capture">media capture</a> mechanism;
   * This is most often used to provide direct camera access on mobile devices.  Note that the accept attribute must
   * be specified and have an associated capture control type (e.g.["image/*"]) for the capture attribute to take effect.  Support may vary by browser.
   *
   */
  capture?: 'user' | 'environment' | 'implementation' | 'none';

  /**
   * Disables the filepicker if set to <code class="prettyprint">true</code>.
   */
  isDisabled?: boolean;

  /**
   * The primary text for the default file picker.
   */
  primaryText?: string;

  /**
   * The secondary text for the default file picker.
   */
  secondaryText?: string;
  /**
   * Whether to allow single or multiple file selection.
   */
  selectionMode?: 'multiple' | 'single';

  /**
   * A label to be used for accessibility purposes. This will be used for the aria-label of the DOM element.
   */
  'aria-label'?: string;

  /**
   * Property that triggers a callback immediately when a set of files have been committed
   */
  onCommit?: (detail: CommitDetail) => void;

  /**
   * Property that triggers a callback immediately when a set of files have been rejected
   */
  onReject?: (detail: RejectDetail) => void;

  /**
   * Width of the File Picker
   */
  width?: Size;

  /**
   * Height of the File Picker
   */
  height?: Size;

  /**
   * If the children is provided, its contents replace the default filepicker.  Filepicker will still set role and aria-label, but
   * the application is responsible for setting the tabindex.
   */
  children?: ComponentChildren;
};

export type CommitDetail = {
  files: FileList;
};

export type RejectDetail = {
  /**
   * Messages that should be displayed to the user (e.g. in an oj-messages component) describing rejected files.
   */
  messages: ComponentMessageItem[];

  /**
   * This property may be populated with a Promise to provide short-term feedback during a user interaction (typically drag and drop); the feedback should be cleared upon the resolution of this Promise.
   */
  until: Promise<void> | null;
};

/**
 * A file picker displays a clickable dropzone for selecting files from the device storage.
 */
export function FilePicker({
  capture = 'none',
  isDisabled = false,
  selectionMode = 'multiple',
  children,
  ...otherProps
}: Props) {
  // this ref is added only for testing purposes.  Allows the test to directly
  // call certain functions that should be tested
  const testHandlersRef = (otherProps as any).__testHandlerSymbol;
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const props = { capture, selectionMode, translations, ...otherProps };
  if (isDisabled) {
    return <DisabledFilePicker {...props}>{children}</DisabledFilePicker>;
  }
  return children ? (
    <CustomFilePicker testHandlersRef={testHandlersRef} {...props}>
      {children}
    </CustomFilePicker>
  ) : (
    <DefaultFilePicker testHandlersRef={testHandlersRef} {...props} />
  );
}

type FilePickerProps = Props & {
  translations: BundleType;
};

type DndProps = {
  onDragEnter: (event: Event) => void;
  onDragOver: (event: DragEvent) => void;
  onDragLeave: (event: DragEvent, mimeTypeDropFail: boolean) => void;
  onDrop: (event: DragEvent) => void;
};

export type TestHandlersRef = ForwardedRef<
  DndProps & {
    onClickSelected: (files: FileList) => void;
  }
>;

type TestHandlerProps = {
  // this ref is populated only in tests
  testHandlersRef?: TestHandlersRef;
};

type DefaultProps = Omit<FilePickerProps, 'isDisabled' | 'children'> & TestHandlerProps;

/**
 * The default file picker element.
 */
const DefaultFilePicker = (props: DefaultProps) => {
  const testIdProps = useTestId(props.testId);
  const { interactionProps, applyActiveStyle, applyHoverStyle, applyPseudoHoverStyle } =
    useInteractionStyle(false);
  const {
    classes,
    styles: {
      maxWidth,
      inner,
      activeStyle,
      innerNeedsEventsHoverStyle,
      innerPseudoHoverStyle,
      invalidDrop,
      validDrop
    }
  } = useComponentTheme<FilePickerVariantOptions, FilePickerStyles>(FilePickerRedwoodTheme, {
    hoveredState: applyHoverStyle ? 'isHover' : undefined,
    pseudoHoveredState: applyPseudoHoverStyle ? 'isPseudoHover' : undefined
  });
  const innerInteractiveClasses = [
    applyActiveStyle ? activeStyle : '',
    applyHoverStyle ? innerNeedsEventsHoverStyle : '',
    applyPseudoHoverStyle ? innerPseudoHoverStyle : ''
  ];
  const [validity, setValidity] = useState<'NA' | 'valid' | 'invalid'>('NA');

  // class added to change bg/border color based on if file is valid on drag and drop
  const validityClass =
    validity === 'valid' ? validDrop : validity === 'invalid' ? invalidDrop : undefined;
  const rootRef = useRef<HTMLDivElement>(null);

  const { validation, dragAndDropProps, pressProps } = useFilepickerElementHelper(
    props,
    rootRef,
    setValidity
  );
  const mergedEventProps = mergeProps(pressProps, interactionProps);

  // These are backdoor handles that are used for testing purposes only
  useSetupTestRef(dragAndDropProps, validation, props.testHandlersRef || null);
  const wrapperClasses = classNames([classes, props.width === undefined && maxWidth]);
  const { isTabbable } = useTabbableMode();
  const { primaryText, secondaryText, translations, selectionMode, width, height } = props;
  return (
    <div
      {...testIdProps}
      {...mergedEventProps}
      class={wrapperClasses}
      tabIndex={isTabbable ? 0 : -1}
      role="button"
      aria-label={getAriaLabel({
        ariaLabel: props['aria-label'],
        isDisabled: false,
        translations,
        primaryText,
        secondaryText,
        selectionMode
      })}
      ref={rootRef}
      style={getStyles(width, height)}>
      <div
        class={classNames([inner, validityClass, ...innerInteractiveClasses])}
        {...dragAndDropProps}>
        <FilePickerDefaultTriggerContent {...props} isDisabled={false} />
      </div>
    </div>
  );
};

type DisabledProps = Omit<
  FilePickerProps,
  'accept' | 'capture' | 'onCommit' | 'onReject' | 'isDisabled'
>;

/**
 * The disabled file picker element.
 */
const DisabledFilePicker = ({
  children,
  primaryText,
  secondaryText,
  translations,
  selectionMode,
  width,
  height,
  testId,
  ...props
}: DisabledProps) => {
  const testIdProps = useTestId(testId);
  const {
    classes,
    styles: { maxWidth, inner, noChildrenMargin }
  } = useComponentTheme<FilePickerVariantOptions, FilePickerStyles>(FilePickerRedwoodTheme, {
    disabledState: 'isDisabled'
  });
  return (
    <div
      class={classNames([classes, inner, noChildrenMargin, width === undefined && maxWidth])}
      role="button"
      aria-label={getAriaLabel({
        ariaLabel: props['aria-label'],
        isDisabled: true,
        translations,
        primaryText,
        secondaryText
      })}
      aria-hidden="true"
      {...testIdProps}
      style={getStyles(width, height)}>
      {children || (
        <FilePickerDefaultTriggerContent {...props} isDisabled={true} translations={translations} />
      )}
    </div>
  );
};

type CustomProps = Omit<FilePickerProps, 'isDisabled' | 'primaryText' | 'secondaryText'> &
  TestHandlerProps;

/**
 * The custom file picker element.
 */
const CustomFilePicker = (props: CustomProps) => {
  const testIdProps = useTestId(props.testId);
  const { interactionProps, applyHoverStyle, applyPseudoHoverStyle } = useInteractionStyle(false);
  const {
    classes,
    styles: { maxWidth, noChildrenMargin }
  } = useComponentTheme<FilePickerVariantOptions, FilePickerStyles>(FilePickerRedwoodTheme, {
    hoveredState: applyHoverStyle ? 'isHover' : undefined,
    pseudoHoveredState: applyPseudoHoverStyle ? 'isPseudoHover' : undefined
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const { validation, dragAndDropProps, pressProps } = useFilepickerElementHelper(props, rootRef);
  const mergedEventProps = mergeProps(pressProps, dragAndDropProps, interactionProps);

  // These are backdoor handles that are used for testing purposes only
  useSetupTestRef(dragAndDropProps, validation, props.testHandlersRef || null);

  const wrapperClasses = classNames([
    classes,
    noChildrenMargin,
    props.width === undefined && maxWidth
  ]);
  const { translations, selectionMode } = props;
  const { isTabbable } = useTabbableMode();
  return (
    <div
      class={classNames([wrapperClasses])}
      tabIndex={isTabbable ? undefined : -1}
      role="button"
      aria-label={getAriaLabel({
        ariaLabel: props['aria-label'],
        isDisabled: false,
        translations,
        selectionMode
      })}
      ref={rootRef}
      style={getStyles(props.width, props.height)}
      {...testIdProps}
      {...mergedEventProps}>
      {props.children}
    </div>
  );
};

/**
 * Only called for testing purposes, sets up the test ref that
 * allows for backdoor access to certain methods
 */
const useSetupTestRef = (
  dragAndDropProps: DndProps,
  validation: (files: FileList) => void,
  testHandlersRef: TestHandlersRef
) => {
  useImperativeHandle(testHandlersRef, () => ({
    onDragEnter: dragAndDropProps.onDragEnter,
    onDragOver: dragAndDropProps.onDragOver,
    onDrop: dragAndDropProps.onDrop,
    onDragLeave: dragAndDropProps.onDragLeave,
    onClickSelected: validation
  }));
};
